import React from 'react';
import { useCommunicationTemplates } from '../../context/CommunicationTemplatesContext';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// Custom upload adapter
class MyUploadAdapter {
  constructor(loader) {
    this.loader = loader; 
  }

  upload() {
    return this.loader.file
      .then(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function() {
          resolve({ default: reader.result });
        };
        reader.onerror = function() {
          reject('Error reading file');
        };
        reader.readAsDataURL(file);
      }));
  }

  abort() {
    // Abort upload process
  }
}

function MyCustomUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new MyUploadAdapter(loader);
  };
}

const CommunicationTemplateForm = () => {
  // eslint-disable-next-line no-unused-vars
  const { templates, addTemplate, getTemplate, updateTemplate } = useCommunicationTemplates();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [editorContent, setEditorContent] = React.useState('');
   

  const schema = yup.object().shape({
    templateName: yup.string().required('Template name is required')
    .test('unique', 'Template name must be unique', function (value) {
      const stored = JSON.parse(localStorage.getItem('CommunicationTemplates') || '[]');
      
      if (!value) return true;
  
      const isDuplicate = stored.some(template => {
        const isSameName = template.templateName.trim().toLowerCase() === value.trim().toLowerCase();
        const isNotSameId = !id || template.id !== id;
        return isSameName && isNotSameId;
      });
  
      return !isDuplicate;
    }),
    recipient: yup.string().required('Recipient is required'),
    subjectLine: yup.string().required('Subject line is required'),
    body: yup.string()
      .required('Body is required')
      .test('not-empty', 'Body is required', value => {
        if (!value) return false;
        // Remove HTML tags and check if there's actual content
        const strippedContent = value.replace(/<[^>]*>/g, '').trim();
        return strippedContent.length > 0;
      }),
    isDefaultTemplate: yup.boolean(),
    isAvailableInWorkerApp: yup.boolean(),
    includeInvoicePDF: yup.boolean(),
    isActive: yup.boolean(),
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    } = useForm({
      resolver: yupResolver(schema),
    defaultValues: {
      isDefaultTemplate: false,
      isAvailableInWorkerApp: false,
      includeInvoicePDF: false,
      isActive: true,
    },
  });

  const isDefaultTemplate = watch('isDefaultTemplate');
  const isAvailableInWorkerApp = watch('isAvailableInWorkerApp');
  const includeInvoicePDF = watch('includeInvoicePDF');
  const isActive = watch('isActive');

  React.useEffect(() => {
    if (id) {
      const template = getTemplate(id);
      if (template) {
        Object.keys(template).forEach(key => {
          setValue(key, template[key]);
        });
        setEditorContent(template.body || '');
      }
    }
  }, [id, getTemplate, setValue]);

  const onSubmit = async (data) => {
    try {
      const templateData = {
        ...data,
        body: editorContent,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.id || 'system'
      };

      if (id) {
        await updateTemplate(id, templateData);
        toast.success('Template updated successfully!');
      } else {
        const newTemplate = {
          ...templateData,
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
          createdBy: user?.id || 'system',
        };
        await addTemplate(newTemplate);
        toast.success('Template created successfully!');
      }
      navigate('/communication-templates');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template. Please try again.');
    }
  };
   
  const handleEditorChange = React.useCallback((event, editor) => {
    const data = editor.getData();
    setEditorContent(data);
    setValue('body', data, { shouldValidate: true });
  }, [setValue]);

  const editorConfiguration = {
    toolbar: {
      items: [
         
        'heading',
        '|',
        'bold',
        'italic',
        'link',
        'bulletedList',
        'numberedList',
        '|',
        'outdent',
        'indent',
        '|',
        'imageUpload',
        'blockQuote',
        'insertTable',
        'undo',
        'redo'
      ]
    },
    image: {
      toolbar: [
        'imageTextAlternative',
        'imageStyle:inline',
        'imageStyle:block',
        'imageStyle:side'
      ],
      upload: {
        types: ['jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff']
      }
    },
    table: {
      contentToolbar: [
        'tableColumn',
        'tableRow',
        'mergeTableCells'
      ]
    },
    language: 'en',
    extraPlugins: [MyCustomUploadAdapterPlugin]
  };
 
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <div className="mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {id ? 'Edit Communication Template' : 'Create New Communication Template'}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Template Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Template Name *</label>
                <input
                  type="text"
                  {...register('templateName')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.templateName ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Template Name"
                />
                {errors.templateName && (
                  <p className="mt-1 text-sm text-red-600">{errors.templateName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Recipient *</label>
                <select
                  {...register('recipient')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.recipient ? 'border-red-500' : ''
                  }`}
                >
                  <option value="">Select Recipient</option>
                  <option value="Customer">Customer</option>
                  <option value="Field Worker">Field Worker</option>
                </select>
                {errors.recipient && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipient.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Subject Line *</label>
                <input
                  type="text"
                  {...register('subjectLine')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.subjectLine ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Subject Line"
                />
                {errors.subjectLine && (
                  <p className="mt-1 text-sm text-red-600">{errors.subjectLine.message}</p>
                )}
              </div>
            </div>

            {/* Template Settings */}
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isDefaultTemplate')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Use As Default Template</label>
                {isDefaultTemplate && (
                  <span className="ml-2 px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                    Default Template
                  </span>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isAvailableInWorkerApp')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Make Available In Worker App</label>
                {isAvailableInWorkerApp && (
                  <span className="ml-2 px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                    Available in Worker App
                  </span>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('includeInvoicePDF')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Include PDF Of Associated Invoice(s)</label>
                {includeInvoicePDF && (
                  <span className="ml-2 px-2 py-1 text-xs font-medium text-purple-800 bg-purple-100 rounded-full">
                    PDF Included
                  </span>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Status</label>
                <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                  isActive 
                    ? 'text-green-800 bg-green-100' 
                    : 'text-red-800 bg-red-100'
                }`}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Template Body */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Body *</label>
            
              <div className={`appearance-none block w-full border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                errors.body ? 'border-red-500' : ''
              }`}>
                <CKEditor
                  editor={ClassicEditor}
                  config={editorConfiguration}
                  data={editorContent}
                  onChange={handleEditorChange}
                />
              </div>
            
            {errors.body && (
              <p className="mt-1 text-sm text-red-600">{errors.body.message}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 mt-6">
            
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
            >
              {id ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommunicationTemplateForm; 